import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Component, Inject, OnInit} from '@angular/core';
import {WorkbenchViewComponent} from '../../../shared/components/viewcontainers/workbench-view.component';
import {WORKBENCH_VIEW_CONFIG, WorkbenchContext, WorkbenchViewConfig} from '../../../shared/types';
import {ProjectId} from '../../../../report/report_types';
import {WorkbenchLayoutManager} from '../../../shared/services/workbench-layout-manager.service';
import {WorkbenchContextService} from '../../../shared/services/workbench-context.service';
import {mergeMap, of} from 'rxjs';
import {Artifact, ArtifactInfo, WellKnownArtifactTypes} from '@webmate-web-core/shared/types/artifact';
import {TestMailService} from '../services/testmail.service';
import {ArtifactId, FindingId, TestEmailAccountId, TestRunId} from '@webmate-web-core/shared/types/ids';
import {TestEmailAccount} from '@webmate-web-core/shared/types/testmgmt';
import {faChevronDown} from '@fortawesome/pro-solid-svg-icons';
import {switchMap, take, tap} from 'rxjs/operators';
import { WebmateWebsocketEvent } from '@webmate-web-core/shared/services/events/events.service';
import {WorkbenchTestService} from '../../../shared/services/workbench-test.service';
import {SelectFindingEvent} from '../../../shared/components/inspector/inspector-shared-tools/select-finding.component';
import {FindingCellInsertService} from '@webmate-web-core/shared/services/finding-cell-insert.service';

@UntilDestroy()
@Component({
    templateUrl: './mail-browser.component.html',
    styleUrls: ['./mail-browser.component.less']
})

export class MailBrowserComponent extends WorkbenchViewComponent implements OnInit {

    workbenchContext: WorkbenchContext;
    projectId: ProjectId = undefined;

    mailAccounts: TestEmailAccount[];
    mailAccountIds: TestEmailAccountId[];
    mailArtifactInfos: ArtifactInfo[];
    mailArtifactIds: ArtifactId[];
    mails: Artifact[];
    oldMails: Artifact[];
    mailsReady: boolean = false;
    mailAccountsReady: boolean = false;
    loadingData: boolean = false;
    creatingAddress: boolean = false;

    currentPage: number = 1;
    rows: number = 8;
    firstObject: number = 0;
    totalItems: number = 0;

    activeTestRun: TestRunId;
    selectedEmailFindings: Record<string, FindingId> = {};
    findingCount: number = 0;

    viewStates: {[mailAccountId: ArtifactId]: boolean} = {};

    constructor(layoutManager: WorkbenchLayoutManager,
                @Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<any>,
                private readonly workbenchContextService: WorkbenchContextService,
                private testMailService: TestMailService,
                private workbenchTestService: WorkbenchTestService,
                private findingCellInsertService: FindingCellInsertService,
    ) {
        super(layoutManager, config);
    }

    ngOnInit() {
        this.projectId = this.workbenchContextService.getProjectId();
        this.getMailAccounts().pipe(untilDestroyed(this),
            switchMap(()=>
                this.testMailService.getNewMailNotification(this.projectId).pipe(
                    switchMap(event =>
                        this.getMailResults().pipe(
                            tap(() => this.addToFinding(event))
                        )
                    )
                )
            )
        ).subscribe();

        this.workbenchContextService.subscribeWorkbenchContext$().subscribe(context => {
            this.activeTestRun = context?.testRunContext?.activeTestRun;
            if (this.activeTestRun) {
                this.selectedEmailFindings = {};
            }
        })
    }

    checkMailId(mail: Artifact, id: TestEmailAccountId){
        return mail.associations.some(assoc => WellKnownArtifactTypes.TestMailAccount && assoc.id === id);
    }

    checkMails(id: TestEmailAccountId) {
        if (this.mails && this.mails.length != 0) {
            for (const mail of this.mails){
                if (this.checkMailId(mail, id)) {
                    return true;
                }
            }
        }
        return false;
    }

    onPageChange($event) {
        this.firstObject = $event.first;
        this.currentPage = $event.page + 1;
        this.getMailResults().pipe(take(1)).subscribe();
    }

    getMailAccounts() {
        this.loadingData = true;
        return this.testMailService.getTestMailAccounts(this.projectId).pipe(untilDestroyed(this), mergeMap(mailAccounts => {
            this.mailAccounts = [];
            this.mailAccountIds = [];
            this.mailAccounts = mailAccounts;
            this.mailAccounts.forEach(a => {
                this.mailAccountIds.push(a.id);
                this.viewStates[a.id] = true;
            });
            if (this.mailAccounts && this.mailAccounts?.length > 0) this.mailAccountsReady = true;
            return this.getMailResults()
        }));
    }

    getMailResults() {
        this.loadingData = true;
        return this.testMailService.getProjectMailArtifacts(this.projectId).pipe(untilDestroyed(this),
            mergeMap(artifactInfos => {
                if (artifactInfos?.length != 0) {
                    this.mailArtifactInfos = [];
                    this.mailArtifactIds = [];
                    artifactInfos.sort((a, b) => new Date(b.creationTime).getTime() - new Date(a.creationTime).getTime());
                    this.mailArtifactInfos = artifactInfos;
                    this.totalItems = artifactInfos.length;
                    this.mailArtifactInfos.forEach(a => this.mailArtifactIds.push(a.id));
                    return this.testMailService.getMailArtifacts(this.mailArtifactIds.slice(this.firstObject, (this.rows * this.currentPage)));
                } else {
                    return of(undefined);
                }
            }),
            tap(mails => {
                this.mails = [];
                this.oldMails = [];
                if (mails && mails.length > 0) {
                    for (const mail of mails) {
                        const isNewMail = mail.associations.some(
                            assoc =>
                                assoc.associationType === WellKnownArtifactTypes.TestMailAccount &&
                                this.mailAccountIds.includes(assoc.id)
                        );
                        if (isNewMail) {
                            this.mails.push(mail);
                        } else {
                            this.oldMails.push(mail);
                        }
                    }
                    this.mailsReady = true;
                }
                this.loadingData = false;
            })
        )
    }

    createNewMailAddress() {
        this.creatingAddress = true;
        this.testMailService.createTestMail(this.projectId).pipe(untilDestroyed(this),switchMap(account => {
            if (account) {
                return this.getMailAccounts();
            } else {
                return of(undefined);
            }
        })).subscribe(()=> this.creatingAddress = false);
    }

    toggleView(mailAccountId: TestEmailAccountId) {
        this.viewStates[mailAccountId] = !this.viewStates[mailAccountId];
    }

    copyToClipboard(text: string): void {
        navigator.clipboard.writeText(text).then(() => {});
    }

    updateFindingsState(event: SelectFindingEvent, mailAccount: TestEmailAccount) {
        if (event.addAutomatically) {
            this.selectedEmailFindings[mailAccount.emailAddress] = event.findingId;
        } else {
            delete this.selectedEmailFindings[mailAccount.emailAddress];
        }
        this.findingCount = event.findingCount;
    }

    addToFinding(event: WebmateWebsocketEvent) {
        const findingId = this.selectedEmailFindings[event.emailAddress];
        if (findingId) {
            this.findingCellInsertService.clearPendingInsertsExcept(findingId);
            this.workbenchTestService.addTestMailToFinding(findingId, event.artifactId).pipe(take(1)).subscribe();
        } else {
            this.findingCellInsertService.clearPendingInsertsExcept();
            this.workbenchTestService.createNewManualFinding(`Finding #${this.findingCount + 1}`).pipe(untilDestroyed(this),
                switchMap(id => this.workbenchTestService.addTestMailToFinding(id, event.artifactId).pipe(take(1)))
            ).subscribe();
        }
    }

    getFindingState (emailAddress: string) {
        return emailAddress in this.selectedEmailFindings;
    }

    protected readonly faChevronDown = faChevronDown;
}
